import React from 'react';
import { st, classes, vars } from './TextComponent.st.css';

import { EllipsisProps, EllipsisState } from '../../Ellipsis.types';
import { TooltipActions } from '../../../../Tooltip';

const isTestEnv = process.env.NODE_ENV === 'test';

type TextComponentProps = Pick<
  EllipsisProps,
  'ellipsis' | 'maxLines' | 'render'
> & {
  textRendered: boolean;
  onTextRendered: () => void;
  textElementRef: React.RefObject<HTMLElement>;
  tooltipRef?: React.RefObject<TooltipActions>;
  isEllipsisActive: boolean;
};

class TextComponent extends React.PureComponent<
  TextComponentProps,
  EllipsisState
> {
  private requestedRecalculationMount: null | number = null;
  private requestedRecalculationUpdate: number[] = [];

  constructor(props: TextComponentProps) {
    super(props);

    this.renderChildren = this.renderChildren.bind(this);
    this.renderElement = this.renderElement.bind(this);
  }

  _getEllipsisClasses = () => {
    const { ellipsis, maxLines } = this.props;
    const ellipsisLines = maxLines && maxLines > 1 ? 'multiline' : 'singleLine';

    return (className?: string) =>
      ellipsis ? st(classes.text, { ellipsisLines }, className) : className;
  };

  componentDidMount() {
    if (!this.props.textRendered) {
      /**
       * The requestAnimationFrame implementation is meant for browser only race condition bug fix.
       * It does not get reproduced in test environment due JSDOM being not a browser.
       * So we skip the requestAnimationFrame part in order not to have delays in calculations.
       */
      if (isTestEnv) {
        return this.props.onTextRendered();
      }
      this.requestedRecalculationMount = requestAnimationFrame(() => {
        this.props.onTextRendered();
      });
    }
  }

  componentDidUpdate() {
    if (!this.props.textRendered) {
      /**
       * The requestAnimationFrame implementation is meant for browser only race condition bug fix.
       * It does not get reproduced in test environment due JSDOM being not a browser.
       * So we skip the requestAnimationFrame part in order not to have delays in calculations.
       */
      if (isTestEnv) {
        return this.props.onTextRendered();
      }
      this.requestedRecalculationUpdate.push(
        requestAnimationFrame(() => {
          this.props.onTextRendered();
        }),
      );
    }
  }

  componentWillUnmount() {
    if (this.requestedRecalculationMount) {
      cancelAnimationFrame(this.requestedRecalculationMount);
    }
    if (this.requestedRecalculationUpdate.length !== 0) {
      this.requestedRecalculationUpdate.forEach(id => cancelAnimationFrame(id));
    }
  }

  renderChildren({
    text,
    suffix,
  }: {
    text: React.ReactNode;
    suffix?: React.ReactNode;
  }): React.ReactNode {
    const { textElementRef, isEllipsisActive, tooltipRef } = this.props;

    if (suffix && isEllipsisActive) {
      const lineHeight =
        textElementRef?.current &&
        window.getComputedStyle(textElementRef.current).lineHeight;

      return (
        <>
          <span
            style={{ shapeOutside: `inset(calc(100% - ${lineHeight}) 0 0)` }}
            className={st(classes.suffix, classes.multilineSuffix)}
            onMouseEnter={() => tooltipRef?.current?.close()}
          >
            {suffix}
          </span>
          <span onMouseEnter={() => tooltipRef?.current?.open()}>{text}</span>
        </>
      );
    } else if (suffix) {
      return (
        <>
          <span>{text}</span>
          <span className={classes.suffix}>{suffix}</span>
        </>
      );
    } else {
      return text;
    }
  }

  renderElement({
    element,
    suffix,
  }: {
    element: React.ReactNode;
    suffix?: React.ReactNode;
  }): React.ReactNode {
    const { maxLines = 1 } = this.props;
    const hasMultilineSuffix = maxLines > 1 && !!suffix;

    return hasMultilineSuffix ? (
      <div className={classes.multilineSuffixWrapper}>{element}</div>
    ) : (
      element
    );
  }

  render() {
    const { render, maxLines, textElementRef } = this.props;

    return render({
      ref: textElementRef,
      ellipsisClasses: this._getEllipsisClasses(),
      ellipsisInlineStyle: { [vars.maxLines]: maxLines },
      renderChildren: this.renderChildren,
      renderElement: this.renderElement,
    });
  }
}

export default TextComponent;
